import React from 'react';
import { Card, CardContent, Typography, Box, TextField, Divider, CircularProgress, Button, Tooltip } from '@mui/material';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import { t } from 'i18next';
import { ILicenseSimpleProps } from '../../../services/LicenseService/types';

type EditableField = 'studentUnitValue' | 'totalValue';

interface LicenseCardProps {
    license: ILicenseSimpleProps;
    isSingleMode: boolean;
    canEditValues: boolean;
    liveCounters?: {
        devicesAmount: number;
        studentsAmount: number;
        responsiblesAmount: number;
    } | null;
    studentUnitValue: number;
    totalValue: number;
    isSaving: boolean;
    error?: string;
    isEditingStudentUnit: boolean;
    isEditingTotalValue: boolean;
    formatCurrency: (value?: number) => string;
    onFieldChange: (license: ILicenseSimpleProps, field: EditableField, rawValue: string) => void;
    onSave: (license: ILicenseSimpleProps) => Promise<void>;
    onSetEditingField: (value: { licenseId: string; field: EditableField } | null) => void;
    onExportLicense: (license: ILicenseSimpleProps) => void;
}

const LicenseCard: React.FC<LicenseCardProps> = ({
    license,
    isSingleMode,
    canEditValues,
    liveCounters,
    studentUnitValue,
    totalValue,
    isSaving,
    error,
    isEditingStudentUnit,
    isEditingTotalValue,
    formatCurrency,
    onFieldChange,
    onSave,
    onSetEditingField,
    onExportLicense,
}) => {
    const useLiveCounters = isSingleMode && !!liveCounters;
    const devicesCountToShow = useLiveCounters ? (liveCounters?.devicesAmount ?? 0) : (license.devicesAmount ?? 0);
    const studentsCountToShow = useLiveCounters ? (liveCounters?.studentsAmount ?? 0) : (license.studentsAmount ?? 0);
    const responsiblesCountToShow = useLiveCounters ? (liveCounters?.responsiblesAmount ?? 0) : (license.responsiblesAmount ?? 0);
    const planLabel = Array.isArray(license.plans) && license.plans.length > 0 ? license.plans.join(', ') : '-';
    const counters = [
        { label: t('financial.usersAmount'), value: license.usersAmount ?? 0 },
        { label: t('financial.devicesAmount'), value: devicesCountToShow },
        { label: t('financial.studentsAmount'), value: studentsCountToShow },
        { label: t('financial.responsiblesAmount'), value: responsiblesCountToShow },
    ];

    return (
        <Card
            elevation={0}
            sx={{
                position: 'relative',
                overflow: 'hidden',
                height: '100%',
                background: 'linear-gradient(180deg, #263044 0%, #1f2937 100%)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: '8px',
                boxShadow: '0 10px 28px rgba(0,0,0,0.22)',
                transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
                '&:hover': {
                    transform: isSingleMode ? 'translateY(-2px)' : 'translateY(-3px)',
                    borderColor: 'rgba(125,211,252,0.35)',
                    boxShadow: '0 16px 36px rgba(0,0,0,0.30)',
                },
            }}
        >
            <Box
                sx={{
                    pt: isSingleMode ? 2.5 : 2,
                    px: isSingleMode ? 2.5 : 2,
                    pb: 1.25,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1.5,
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: '#7dd3fc',
                            fontWeight: 800,
                            letterSpacing: 0,
                            display: 'block',
                            mb: 0.5,
                            overflowWrap: 'anywhere',
                        }}
                    >
                        {planLabel}
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            color: '#fff',
                            fontWeight: 800,
                            lineHeight: 1.18,
                            overflowWrap: 'anywhere',
                        }}
                    >
                        {license.name ?? t('financial.noName')}
                    </Typography>
                </Box>

                <Box sx={{ flexShrink: 0 }}>
                    <Tooltip title="Gerar Excel desta licença">
                        <Button
                            size="medium"
                            variant="text"
                            onClick={() => onExportLicense(license)}
                            sx={{
                                minWidth: 34,
                                width: 34,
                                height: 34,
                                p: 0,
                                borderRadius: '8px',
                                color: '#e0f2fe',
                                border: '1px solid rgba(125,211,252,0.28)',
                                backgroundColor: 'rgba(14,165,233,0.12)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': {
                                    backgroundColor: 'rgba(14,165,233,0.22)',
                                    borderColor: 'rgba(125,211,252,0.48)',
                                },
                            }}
                        >
                            <FileDownloadRoundedIcon fontSize="small" />
                        </Button>
                    </Tooltip>
                </Box>
            </Box>

            <CardContent sx={{ pt: 0, px: isSingleMode ? 2.5 : 2, pb: isSingleMode ? 2.5 : 2 }}>
                <Box
                    sx={{
                        px: 1.25,
                        py: 1,
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.08)',
                        backgroundColor: 'rgba(15,23,42,0.42)',
                    }}
                >
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.54)', fontWeight: 700 }}>
                        License ID
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            mt: 0.35,
                            color: 'rgba(255,255,255,0.88)',
                            fontFamily: 'monospace',
                            overflowWrap: 'anywhere',
                            lineHeight: 1.35,
                        }}
                    >
                        {license.id}
                    </Typography>
                </Box>

                <Box mt={1.5} display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                    {counters.map((counter) => (
                        <Box
                            key={counter.label}
                            sx={{
                                p: isSingleMode ? 1.25 : 1,
                                minHeight: 74,
                                borderRadius: '8px',
                                border: '1px solid rgba(255,255,255,0.08)',
                                backgroundColor: 'rgba(255,255,255,0.045)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.62)', lineHeight: 1.25 }}>
                                {counter.label}
                            </Typography>
                            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.92)', fontWeight: 850, lineHeight: 1.15 }}>
                                {counter.value}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <Divider sx={{ my: isSingleMode ? 2 : 1.5, borderColor: 'rgba(255,255,255,0.10)' }} />

                <Box mt={1} display="grid" gridTemplateColumns="1fr 1fr" gap={isSingleMode ? 1.2 : 1}>
                    <Box sx={{ p: isSingleMode ? 1.25 : 1, borderRadius: '8px', backgroundColor: 'rgba(15,23,42,0.40)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.62)' }}>
                            {t('financial.studentUnitValue')}
                        </Typography>
                        {isEditingStudentUnit ? (
                            <TextField
                                autoFocus
                                type="number"
                                size="small"
                                fullWidth
                                value={studentUnitValue}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => onFieldChange(license, 'studentUnitValue', e.target.value)}
                                onBlur={async () => {
                                    await onSave(license);
                                    onSetEditingField(null);
                                }}
                                onKeyDown={async (e) => {
                                    if (e.key === 'Enter') {
                                        await onSave(license);
                                        onSetEditingField(null);
                                    }

                                    if (e.key === 'Escape') {
                                        onSetEditingField(null);
                                    }
                                }}
                                inputProps={{ min: 0, step: '0.01' }}
                                sx={{
                                    mt: 0.5,
                                    '& input': { color: '#fff', fontWeight: 700 },
                                    '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.35)' },
                                }}
                            />
                        ) : (
                            <Typography
                                variant="body1"
                                sx={{
                                    color: '#e0f2fe',
                                    fontWeight: 800,
                                    mt: 0.5,
                                    textDecoration: 'underline',
                                    textUnderlineOffset: '3px',
                                    cursor: canEditValues ? 'pointer' : 'default',
                                    transition: 'opacity 0.2s ease',
                                    '&:hover': { opacity: canEditValues ? 0.85 : 1 },
                                }}
                                onClick={() => {
                                    if (canEditValues) {
                                        onSetEditingField({ licenseId: license.id, field: 'studentUnitValue' });
                                    }
                                }}
                            >
                                {formatCurrency(studentUnitValue)}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ p: isSingleMode ? 1.25 : 1, borderRadius: '8px', backgroundColor: 'rgba(34,197,94,0.11)', border: '1px solid rgba(134,239,172,0.28)' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                            {t('financial.totalValue')}
                        </Typography>
                        {isEditingTotalValue ? (
                            <TextField
                                autoFocus
                                type="number"
                                size="small"
                                fullWidth
                                value={totalValue}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => onFieldChange(license, 'totalValue', e.target.value)}
                                onBlur={async () => {
                                    await onSave(license);
                                    onSetEditingField(null);
                                }}
                                onKeyDown={async (e) => {
                                    if (e.key === 'Enter') {
                                        await onSave(license);
                                        onSetEditingField(null);
                                    }

                                    if (e.key === 'Escape') {
                                        onSetEditingField(null);
                                    }
                                }}
                                inputProps={{ min: 0, step: '0.01' }}
                                sx={{
                                    mt: 0.5,
                                    '& input': { color: '#fff', fontWeight: 800 },
                                    '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.35)' },
                                }}
                            />
                        ) : (
                            <Typography
                                variant="body1"
                                sx={{
                                    color: '#bbf7d0',
                                    fontWeight: 800,
                                    mt: 0.5,
                                    textDecoration: 'underline',
                                    textUnderlineOffset: '3px',
                                    cursor: canEditValues ? 'pointer' : 'not-allowed',
                                    opacity: canEditValues ? 1 : 0.7,
                                    transition: 'opacity 0.2s ease',
                                    '&:hover': { opacity: canEditValues ? 0.88 : 0.7 },
                                }}
                                onClick={() => {
                                    if (canEditValues) {
                                        onSetEditingField({ licenseId: license.id, field: 'totalValue' });
                                    }
                                }}
                            >
                                {formatCurrency(totalValue)}
                            </Typography>
                        )}
                        {isSaving && (
                            <Box mt={0.5} display="flex" alignItems="center" gap={0.75}>
                                <CircularProgress size={14} sx={{ color: 'rgba(255,255,255,0.8)' }} />
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                                    Salvando...
                                </Typography>
                            </Box>
                        )}
                        {error && (
                            <Typography variant="caption" sx={{ color: '#ffb4b4', display: 'block', mt: 0.5 }}>
                                {error}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default React.memo(LicenseCard);
